declare const brandSymbol: unique symbol

/** Nominal marker. Attach with `Branded<T, 'Name'>`. */
export type Brand<B extends string> = { readonly [brandSymbol]: B }

export type Branded<T, B extends string> = T & Brand<B>

/** Any branded integer quantity — Count, Wp, Millilitres, Minutes, Pence. */
export type IntegerQuantity = Branded<number, string>

export class BrandRangeError extends Error {
  constructor(brandName: string, value: number, reason: string) {
    super(`${brandName}: ${reason} (received ${String(value)})`)
    this.name = 'BrandRangeError'
  }
}

export interface IntegerBrand<B extends string> {
  readonly name: B
  /** Validating constructor. The only sanctioned way to produce this brand. */
  of(value: number): Branded<number, B>
  is(value: number): value is Branded<number, B>
}

export function brandedInteger<B extends string>(
  name: B,
  opts: { min?: number; max?: number } = {},
): IntegerBrand<B> {
  const min = opts.min ?? Number.MIN_SAFE_INTEGER
  const max = opts.max ?? Number.MAX_SAFE_INTEGER

  const is = (value: number): value is Branded<number, B> =>
    Number.isSafeInteger(value) && value >= min && value <= max

  return {
    name,
    is,
    of(value: number): Branded<number, B> {
      if (!Number.isSafeInteger(value)) {
        throw new BrandRangeError(name, value, 'must be a safe integer')
      }
      if (value < min || value > max) {
        throw new BrandRangeError(name, value, `must be within [${min}, ${max}]`)
      }
      return value as Branded<number, B>
    },
  }
}
